import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaEnvelopeOpenText, FaPlus, FaPlay, FaSyncAlt } from "react-icons/fa";
import Spinner from "../components/Spinner";
import ErrorPage from "../components/ErrorPage";
import Modal from "../components/Modal";
import EmailValidationChecksPanel from "../components/emailValidation/EmailValidationChecksPanel";
import { LdsOverviewHero, LdsOverviewPage } from "../components/lds/LdsOverview";
import {
  LdsTable,
  LdsTableBody,
  LdsTableCard,
  LdsTableCell,
  LdsTableHead,
  LdsTableHeadCell,
  LdsTableRow,
  LdsTableScroll,
} from "../components/lds/LdsTable";
import http from "../api/apiClient";
import { mapHttpErrorToMessage } from "../utils/mapHttpErrorToMessage";

const API_PATH = "/admin/email-validation/domains";
const STATS_PATH = "/admin/email-validation/stats";

type ActiveTab = "checks" | "rules";
type EditorMode = "create" | "edit";

type EmailValidationRule = Record<string, any> & {
  id?: string | number | null;
  domain?: string | null;
  enabled?: boolean | number | string | null;
  active?: boolean | number | string | null;
  reason?: string | null;
  status?: string | null;
  matchType?: string | null;
  match_type?: string | null;
};

type FormState = {
  id: string | number | null;
  domain: string;
  suggestedDomain: string;
  enabled: boolean;
  reason: string;
  status: string;
  matchType: string;
};

type MessageLike = {
  message?: string;
  title?: string;
  status?: number;
};

const pickIdentity = (rule?: EmailValidationRule | null) =>
  rule?.id ??
  rule?.domain ??
  rule?.sourceDomain ??
  rule?.fromDomain ??
  null;

const extractLastRunAt = (payload: any) => {
  if (!payload || typeof payload !== "object") return null;
  const candidates = [
    payload.lastRunAt,
    payload.last_run_at,
    payload.lastValidationAt,
    payload.last_validation_at,
    payload.validatedAt,
    payload.validated_at,
    payload.ranAt,
    payload.ran_at,
    payload.lastExecutedAt,
    payload.last_executed_at,
    payload.meta?.lastRunAt,
    payload.meta?.last_run_at,
  ].filter((v) => v != null && v !== "");
  return candidates.length ? candidates[0] : null;
};

const extractNextRunAt = (payload: any) => {
  if (!payload || typeof payload !== "object") return null;
  const candidates = [
    payload.nextRunAt,
    payload.next_run_at,
    payload.nextSyncAt,
    payload.next_sync_at,
    payload.nextScheduledAt,
    payload.next_scheduled_at,
    payload.nextExecutionAt,
    payload.next_execution_at,
    payload.meta?.nextRunAt,
    payload.meta?.next_run_at,
    payload.meta?.nextSyncAt,
    payload.meta?.next_sync_at,
  ].filter((v) => v != null && v !== "");
  return candidates.length ? candidates[0] : null;
};

const toDate = (value: any): Date | null => {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === "number" && Number.isFinite(value)) {
    // Accept epoch seconds/millis/micros/nanos
    const abs = Math.abs(value);
    const ms =
      abs >= 1e17 ? Math.floor(value / 1_000_000) : // nanos -> ms
      abs >= 1e14 ? Math.floor(value / 1_000) : // micros -> ms
      abs >= 1e12 ? Math.floor(value) : // millis
      abs >= 1e9 ? Math.floor(value * 1_000) : // seconds
      Math.floor(value); // fallback
    const d = new Date(ms);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (typeof value === "string") {
    const s = value.trim();
    if (s && /^-?\d+(\.\d+)?$/.test(s)) {
      const n = Number(s);
      if (Number.isFinite(n)) return toDate(n);
    }
  }
  if (Array.isArray(value)) {
    const [y, m, d, hh = 0, mm = 0, ss = 0, sub = 0] = value;
    if (!y || !m || !d) return null;
    const ms =
      typeof sub === "number"
        ? sub > 1000 ? Math.floor(sub / 1_000_000) : Math.floor(sub)
        : 0;
    const date = new Date(Number(y), Number(m) - 1, Number(d), Number(hh), Number(mm), Number(ss), ms);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

const formatDateTime = (value: any, locale = "nl-NL") => {
  if (!value) return "";
  const d = toDate(value);
  if (!d) return "";
  return d.toLocaleString(locale, { dateStyle: "medium", timeStyle: "short" });
};

const renderValue = (value: any) => {
  if (value == null || value === "") return "—";
  if (typeof value === "boolean") return value ? "true" : "false";
  if (typeof value === "number") return String(value);
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

const pickSuggestedDomain = (rule?: EmailValidationRule | null) =>
  rule?.suggestedDomain ??
  rule?.suggested_domain ??
  rule?.targetDomain ??
  rule?.toDomain ??
  rule?.correctedDomain ??
  rule?.corrected_domain ??
  "";

const pickEnabled = (rule?: EmailValidationRule | null) => {
  const v = rule?.enabled ?? rule?.active ?? null;
  if (typeof v === "boolean") return v;
  if (typeof v === "number") return v !== 0;
  if (typeof v === "string") return ["true", "1", "yes", "on"].includes(v.trim().toLowerCase());
  return true;
};

const pickMatchType = (rule?: EmailValidationRule | null) =>
  rule?.matchType ??
  rule?.match_type ??
  rule?.type ??
  null;

const pickStatus = (rule?: EmailValidationRule | null) => {
  const raw = rule?.status ?? rule?.state ?? null;
  if (raw == null) return "";
  return String(raw).trim().toLowerCase();
};

const deriveMatchTypeFromDomainInput = (domainInput: string) => {
  const raw = String(domainInput || "").trim();
  if (!raw) return null;
  if (raw.startsWith("*.") || raw.startsWith(".")) return "SUFFIX";
  return null;
};

const normalizeDomainInputForDisplay = (domainInput: string) => String(domainInput || "").trim().toLowerCase();

const normalizeStatsPayload = (payload: any) => {
  if (!payload || typeof payload !== "object") return null;
  const pickContainer = (obj: any) => (obj && typeof obj === "object" ? obj : null);
  const totals = pickContainer(payload.totals);
  const counts = pickContainer(payload.counts);
  const statusCounts = pickContainer(payload.statusCounts) || pickContainer(payload.status_counts);
  const byStatus = pickContainer(payload.byStatus) || pickContainer(payload.by_status);

  const upper =
    (byStatus &&
      Object.keys(byStatus).some((k) => k === "VALID" || k === "INVALID" || k === "SUSPICIOUS")) ||
    (statusCounts &&
      Object.keys(statusCounts).some((k) => k === "VALID" || k === "INVALID" || k === "SUSPICIOUS"))
      ? (byStatus || statusCounts)
      : null;

  const pickNumber = (...candidates: any[]) => {
    for (const v of candidates) {
      if (typeof v === "number" && Number.isFinite(v)) return v;
      if (typeof v === "string" && v.trim() && !Number.isNaN(Number(v))) return Number(v);
    }
    return null;
  };
  const total = pickNumber(
    payload.total,
    payload.totalCount,
    payload.total_count,
    payload.totalWithEmail,
    payload.total_with_email,
    payload.countTotal,
    payload.count_total,
    totals?.total,
    totals?.totalCount,
    totals?.total_count,
    totals?.totalWithEmail,
    totals?.total_with_email,
    counts?.total,
    counts?.totalCount,
    counts?.total_count,
    counts?.totalWithEmail,
    counts?.total_with_email
  );
  const valid = pickNumber(
    payload.valid,
    payload.validCount,
    payload.valid_count,
    totals?.valid,
    totals?.validCount,
    totals?.valid_count,
    counts?.valid,
    counts?.validCount,
    counts?.valid_count,
    statusCounts?.valid,
    statusCounts?.validCount,
    statusCounts?.valid_count,
    upper?.VALID
  );
  const invalid = pickNumber(
    payload.invalid,
    payload.invalidCount,
    payload.invalid_count,
    totals?.invalid,
    totals?.invalidCount,
    totals?.invalid_count,
    counts?.invalid,
    counts?.invalidCount,
    counts?.invalid_count,
    statusCounts?.invalid,
    statusCounts?.invalidCount,
    statusCounts?.invalid_count,
    upper?.INVALID
  );
  const suspicious = pickNumber(
    payload.suspicious,
    payload.suspiciousCount,
    payload.suspicious_count,
    totals?.suspicious,
    totals?.suspiciousCount,
    totals?.suspicious_count,
    counts?.suspicious,
    counts?.suspiciousCount,
    counts?.suspicious_count,
    statusCounts?.suspicious,
    statusCounts?.suspiciousCount,
    statusCounts?.suspicious_count,
    upper?.SUSPICIOUS
  );
  if ([total, valid, invalid, suspicious].every((v) => v == null)) return null;
  return {
    total: total ?? 0,
    valid: valid ?? 0,
    invalid: invalid ?? 0,
    suspicious: suspicious ?? 0,
  };
};

const AdminEmailValidationDomains = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<ActiveTab>("checks"); // rules | checks
  const [checksRefreshToken, setChecksRefreshToken] = useState(0);
  const [rules, setRules] = useState<EmailValidationRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [err, setErr] = useState<MessageLike | string | null>(null);
  const [running, setRunning] = useState(false);
  const [runFeedback, setRunFeedback] = useState<string | null>(null);
  const [lastRunAt, setLastRunAt] = useState<any>(null);
  const [nextRunAt, setNextRunAt] = useState<any>(null);
  const [statsPayload, setStatsPayload] = useState<any>(null);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editorMode, setEditorMode] = useState<EditorMode>("create"); // create | edit
  const [editorError, setEditorError] = useState<string | null>(null);
  const [activeRule, setActiveRule] = useState<EmailValidationRule | null>(null);
  const [saving, setSaving] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [form, setForm] = useState<FormState>({
    id: null,
    domain: "",
    suggestedDomain: "",
    enabled: true,
    reason: "",
    status: "suspicious",
    matchType: "",
  });

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const derivedStats = useMemo(() => {
    const items = Array.isArray(rules) ? rules : [];
    const total = items.length;
    let invalid = 0;
    let suspicious = 0;
    let valid = 0;
    items.forEach((r) => {
      const status = pickStatus(r);
      if (status === "invalid") invalid += 1;
      if (status === "suspicious") suspicious += 1;
      if (status === "valid") valid += 1;
    });
    return { total, invalid, suspicious, valid };
  }, [rules]);

  const stats = useMemo(() => {
    const normalized = normalizeStatsPayload(statsPayload);
    if (normalized) return normalized;
    return derivedStats;
  }, [statsPayload, derivedStats]);

  const apiPrefersSnakeCase = useMemo(() => {
    const sample = Array.isArray(rules) ? rules[0] : null;
    if (!sample || typeof sample !== "object") return false;
    if ("suggested_domain" in sample || "match_type" in sample) return true;
    if ("suggestedDomain" in sample || "matchType" in sample) return false;
    return false;
  }, [rules]);

  const columns = useMemo(() => {
    const keys = new Set<string>();
    (Array.isArray(rules) ? rules : []).forEach((item) => {
      Object.keys(item || {}).forEach((k) => keys.add(k));
    });
    // Keep the table compact; exclude noisy audit columns by default.
    const excluded = new Set([
      "createdAt",
      "created_at",
      "updatedAt",
      "updated_at",
      "deletedAt",
      "deleted_at",
      "id",
      "version",
    ]);
    excluded.forEach((k) => keys.delete(k));

    const preferred = [
      "domain",
      "matchType",
      "match_type",
      "suggestedDomain",
      "suggested_domain",
      "targetDomain",
      "toDomain",
      "correctedDomain",
      "enabled",
      "active",
      "reason",
      "status",
    ];
    const orderedPreferred = preferred.filter((k) => keys.has(k));
    const remaining = Array.from(keys)
      .filter((k) => !preferred.includes(k))
      .filter((k) => !excluded.has(k))
      .sort();
    return [...orderedPreferred, ...remaining];
  }, [rules]);

  const headerLabelForColumn = (column: string) => {
    const c = String(column || "");
    switch (c) {
      case "domain":
        return t("admin.email_validation.columns.domain", { defaultValue: "Domein" });
      case "suggestedDomain":
      case "suggested_domain":
      case "targetDomain":
      case "toDomain":
      case "correctedDomain":
        return t("admin.email_validation.columns.suggestedDomain", { defaultValue: "Suggestie" });
      case "enabled":
      case "active":
        return t("admin.email_validation.columns.enabled", { defaultValue: "Actief" });
      case "reason":
        return t("admin.email_validation.columns.reason", { defaultValue: "Reden" });
      case "status":
        return t("admin.email_validation.columns.status", { defaultValue: "Status" });
      case "matchType":
      case "match_type":
      case "type":
        return t("admin.email_validation.columns.matchType", { defaultValue: "Match type" });
      default:
        return c;
    }
  };

  const fetchStats = async () => {
    try {
      const { data } = await http.get(STATS_PATH);
      setStatsPayload(data ?? null);
      const extractedLast = extractLastRunAt(data);
      if (extractedLast != null) setLastRunAt(extractedLast);
      const extractedNext = extractNextRunAt(data);
      if (extractedNext != null) setNextRunAt(extractedNext);
    } catch {
      // Non-blocking: derive stats from rules as fallback.
      setStatsPayload(null);
    }
  };

  const fetchRules = async ({ refresh = false }: { refresh?: boolean } = {}) => {
    setErr(null);
    if (refresh) setRunFeedback(null);
    if (refresh) setRefreshing(true);
    else setLoading(true);
    try {
      const { data } = await http.get(API_PATH);
      if (Array.isArray(data)) {
        setRules(data);
      } else if (data && typeof data === "object") {
        const items = data.items || data.rules || data.data || [];
        setRules(Array.isArray(items) ? items : []);
        const extracted = extractLastRunAt(data);
        if (extracted != null) setLastRunAt(extracted);
      } else {
        setRules([]);
      }
      await fetchStats();
    } catch (e: any) {
      setErr(mapHttpErrorToMessage(e, t));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchRules();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openCreate = () => {
    setEditorMode("create");
    setActiveRule(null);
    setEditorError(null);
    setAdvancedOpen(false);
    setForm({
      id: null,
      domain: "",
      suggestedDomain: "",
      enabled: true,
      reason: "",
      status: "suspicious",
      matchType: "",
    });
    setEditorOpen(true);
  };

  const openEdit = (rule: EmailValidationRule) => {
    setEditorMode("edit");
    setActiveRule(rule || null);
    setEditorError(null);
    setAdvancedOpen(false);
    setForm({
      id: rule?.id ?? null,
      domain: rule?.domain ?? "",
      suggestedDomain: pickSuggestedDomain(rule),
      enabled: pickEnabled(rule),
      reason: rule?.reason ?? "",
      status: rule?.status ?? "suspicious",
      matchType: String(pickMatchType(rule) || ""),
    });
    setEditorOpen(true);
  };

  const requestDelete = (rule: EmailValidationRule) => {
    setActiveRule(rule || null);
    setDeleteOpen(true);
  };

  useEffect(() => {
    if (!editorOpen) return undefined;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setEditorOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [editorOpen]);

  const saveRule = async () => {
    if (saving) return;
    setSaving(true);
    setEditorError(null);
    try {
      const normalizedDomain = normalizeDomainInputForDisplay(form.domain);
      const inferredMatchType = deriveMatchTypeFromDomainInput(normalizedDomain);
      const chosenMatchType = String(form.matchType || "").trim().toUpperCase();
      const matchType = inferredMatchType || (chosenMatchType ? chosenMatchType : null);

      const payloadBase = {
        domain: normalizedDomain,
        enabled: Boolean(form.enabled),
        reason: String(form.reason || "").trim() || null,
        status: String(form.status || "").trim(),
      };
      const suggestedDomain = String(form.suggestedDomain || "").trim() || null;
      const payloadWithoutId = apiPrefersSnakeCase
        ? {
            ...payloadBase,
            suggested_domain: suggestedDomain,
            ...(matchType ? { match_type: matchType } : null),
          }
        : {
            ...payloadBase,
            suggestedDomain,
            ...(matchType ? { matchType } : null),
          };
      const payload =
        editorMode === "edit"
          ? payloadWithoutId
          : form.id != null
            ? { id: form.id, ...payloadWithoutId }
            : payloadWithoutId;
      if (!payload.domain) {
        setEditorError(t("admin.email_validation.domain_required", { defaultValue: "Domain is verplicht." }));
        return;
      }
      if (!["valid", "invalid", "suspicious"].includes(payload.status)) {
        setEditorError(t("admin.email_validation.status_invalid", { defaultValue: "Status is ongeldig." }));
        return;
      }
      const mt = (payload as any).matchType || (payload as any).match_type;
      if (mt && !["EXACT", "SUFFIX"].includes(mt)) {
        setEditorError(t("admin.email_validation.match_type_invalid", { defaultValue: "Match type is ongeldig." }));
        return;
      }
      if (editorMode === "edit") {
        const id = form.id ?? activeRule?.id ?? null;
        if (id == null || id === "") {
          setEditorError(t("admin.email_validation.id_required", { defaultValue: "Id ontbreekt voor update." }));
          return;
        }
        const idStr = String(id).trim();
        if (!/^\d+$/.test(idStr)) {
          setEditorError(
            t("admin.email_validation.id_must_be_number", {
              defaultValue: `Id moet numeriek zijn (backend verwacht Long). Ontvangen: ${idStr}`,
            })
          );
          return;
        }
        await http.put(`${API_PATH}/${encodeURIComponent(idStr)}`, payload);
      } else {
        await http.post(API_PATH, payload);
      }

      setEditorOpen(false);
      setActiveRule(null);
      setAdvancedOpen(false);
      await fetchRules({ refresh: true });
    } catch (e: any) {
      const apiMsg =
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        e?.response?.data?.title ||
        null;
      const status = e?.response?.status;
      const fallback = mapHttpErrorToMessage(e, t)?.message || String(e?.message || e);
      setEditorError(
        apiMsg
          ? (status ? `HTTP ${status}: ${apiMsg}` : String(apiMsg))
          : fallback
      );
    } finally {
      setSaving(false);
    }
  };

  const deleteRule = async () => {
    if (deleting) return;
    setDeleting(true);
    setErr(null);
    try {
      const id = pickIdentity(activeRule);
      if (id == null) {
        setErr({ message: t("admin.email_validation.id_required", { defaultValue: "Id ontbreekt voor delete." }) });
        return;
      }
      await http.delete(`${API_PATH}/${encodeURIComponent(String(id))}`);

      setDeleteOpen(false);
      setActiveRule(null);
      await fetchRules({ refresh: true });
    } catch (e: any) {
      setErr(mapHttpErrorToMessage(e, t));
    } finally {
      setDeleting(false);
    }
  };

  const runValidation = async () => {
    if (running) return;
    setRunning(true);
    setErr(null);
    setRunFeedback(null);
    try {
      const res = await http.post("/admin/email-validation/run");
      const extracted = extractLastRunAt(res?.data);
      if (extracted != null) setLastRunAt(extracted);
      const msg =
        res?.data?.message ||
        res?.data?.status ||
        t("admin.email_validation.run_started", { defaultValue: "Run gestart." });
      setRunFeedback(String(msg));
    } catch (e: any) {
      setErr(mapHttpErrorToMessage(e, t));
    } finally {
      setRunning(false);
    }
  };

  if (loading) return <Spinner />;
  if (err && !Array.isArray(rules)) {
    const message = typeof err === "string" ? err : err.message || err.title || "Error";
    return <ErrorPage status={typeof err === "string" ? undefined : err.status} message={message} onRetry={() => fetchRules()} />;
  }

  return (
    <LdsOverviewPage>
      <LdsOverviewHero
        icon={FaEnvelopeOpenText}
        eyebrow={t("admin.email_validation.eyebrow", { defaultValue: "Maintenance" })}
        title={t("admin.email_validation.title", { defaultValue: "Email validation domains" })}
        description={t("admin.email_validation.description", {
          defaultValue: "Beheer domeinregels en controleer e-mailvalidatie.",
        })}
        actions={
          <>
            <button
              type="button"
              onClick={async () => {
                await fetchRules({ refresh: true });
                setChecksRefreshToken((v) => v + 1);
              }}
              disabled={refreshing}
              className="lumen-glow-sheen inline-flex items-center gap-2 rounded-full border border-indigo-100/60 bg-white/80 px-4 py-2 text-sm font-semibold text-indigo-700 transition hover:text-indigo-900 disabled:opacity-60 dark:border-indigo-500/40 dark:bg-indigo-900/40 dark:text-indigo-200"
            >
              <FaSyncAlt aria-hidden="true" className={refreshing ? "animate-spin" : ""} />
              {refreshing ? t("common.loading", { defaultValue: "Loading…" }) : t("common.actions.refresh", { defaultValue: "Refresh" })}
            </button>
            <button
              type="button"
              onClick={runValidation}
              disabled={running}
              className="lumen-glow-sheen inline-flex items-center gap-2 rounded-full border border-amber-200/60 bg-amber-50/80 px-4 py-2 text-sm font-semibold text-amber-800 transition hover:text-amber-900 disabled:opacity-60 dark:border-amber-500/40 dark:bg-amber-900/25 dark:text-amber-100"
              title={t("admin.email_validation.run_hint", { defaultValue: "POST /api/v1/admin/email-validation/run" })}
            >
              <FaPlay aria-hidden="true" />
              {running
                ? t("admin.email_validation.running", { defaultValue: "Run bezig…" })
                : t("admin.email_validation.run", { defaultValue: "Run validatie" })}
            </button>
            {activeTab === "rules" ? (
              <button
                type="button"
                onClick={openCreate}
                className="lumen-glow-button inline-flex items-center gap-2 text-sm"
              >
                <FaPlus aria-hidden="true" />
                {t("admin.email_validation.new_rule_button", { defaultValue: "Nieuwe regel" })}
              </button>
            ) : null}
          </>
        }
      />

      <div className="space-y-4">
        <div className="rounded-[24px] border border-slate-200/80 bg-white/95 p-4 shadow-sm dark:border-white/10 dark:bg-slate-900/80">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-[260px]">
              <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                {activeTab === "rules"
                  ? t("admin.email_validation.hint", {
                      defaultValue: "Beheer domeinregels voor email-validatie (typo’s, blokkades, suggesties).",
                    })
                  : t("admin.email_validation.checks.title", { defaultValue: "E-mail validatie checks" })}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {activeTab === "rules"
                  ? t("admin.email_validation.endpoint", { defaultValue: "API: /api/v1/admin/email-validation/domains" })
                  : t("admin.email_validation.checks.subtitle", {
                      defaultValue: "Bekijk onjuiste, huidige en pending e-mailchecks (max 500).",
                    })}
              </p>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {t("admin.email_validation.last_run", { defaultValue: "Laatste validatie" })}:{" "}
                {lastRunAt ? formatDateTime(lastRunAt) : t("common.unknown", { defaultValue: "Onbekend" })}
              </p>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {t("admin.email_validation.next_sync", { defaultValue: "Volgende sync" })}:{" "}
                {nextRunAt ? formatDateTime(nextRunAt) : t("common.unknown", { defaultValue: "Onbekend" })}
              </p>
            </div>

            <div className="rounded-full bg-indigo-50 px-4 py-2 text-sm font-semibold text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-200">
              {stats.total} {t("admin.email_validation.stats.total", { defaultValue: "Totaal" }).toLowerCase()}
            </div>
          </div>

          <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
            <div className="rounded-2xl border border-slate-200/70 bg-white/70 px-4 py-3 shadow-sm dark:border-slate-700/60 dark:bg-slate-950/10">
              <div className="text-xs font-semibold text-slate-500 dark:text-slate-400">
                {t("admin.email_validation.stats.total", { defaultValue: "Totaal" })}
              </div>
              <div className="mt-1 text-2xl font-extrabold text-slate-900 dark:text-slate-100">
                {stats.total}
              </div>
            </div>

            <div className="rounded-2xl border border-emerald-200/70 bg-emerald-50/60 px-4 py-3 shadow-sm dark:border-emerald-500/30 dark:bg-emerald-950/10">
              <div className="text-xs font-semibold text-emerald-800/80 dark:text-emerald-100/90">
                {t("admin.email_validation.stats.valid", { defaultValue: "Valide" })}
              </div>
              <div className="mt-1 text-2xl font-extrabold text-emerald-950 dark:text-emerald-50">
                {stats.valid}
              </div>
            </div>

            <div className="rounded-2xl border border-red-200/70 bg-red-50/60 px-4 py-3 shadow-sm dark:border-red-500/30 dark:bg-red-950/10">
              <div className="text-xs font-semibold text-red-700/80 dark:text-red-200/90">
                {t("admin.email_validation.stats.invalid", { defaultValue: "Onjuist" })}
              </div>
              <div className="mt-1 text-2xl font-extrabold text-red-900 dark:text-red-100">
                {stats.invalid}
              </div>
            </div>

            <div className="rounded-2xl border border-amber-200/70 bg-amber-50/60 px-4 py-3 shadow-sm dark:border-amber-500/30 dark:bg-amber-950/10">
              <div className="text-xs font-semibold text-amber-800/80 dark:text-amber-100/90">
                {t("admin.email_validation.stats.suspicious", { defaultValue: "Twijfelachtig" })}
              </div>
              <div className="mt-1 text-2xl font-extrabold text-amber-950 dark:text-amber-50">
                {stats.suspicious}
              </div>
            </div>
          </div>

          <div className="mt-4 flex flex-wrap gap-2 rounded-2xl bg-white/70 p-2 dark:bg-gray-800/60">
            <button
              type="button"
              onClick={() => setActiveTab("checks")}
              className={`min-w-[170px] flex-1 rounded-xl border px-4 py-2 text-sm font-semibold transition ${
                activeTab === "checks"
                  ? "border-indigo-600 bg-indigo-600 text-white shadow-lg shadow-indigo-500/30 dark:border-indigo-500 dark:bg-indigo-500"
                  : "border-indigo-100/60 bg-white/80 text-gray-700 hover:bg-white hover:text-indigo-700 dark:border-indigo-500/40 dark:bg-indigo-900/30 dark:text-indigo-200 dark:hover:bg-indigo-900/40"
              }`}
            >
              {t("admin.email_validation.tabs.checks", { defaultValue: "Checks" })}
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("rules")}
              className={`min-w-[170px] flex-1 rounded-xl border px-4 py-2 text-sm font-semibold transition ${
                activeTab === "rules"
                  ? "border-indigo-600 bg-indigo-600 text-white shadow-lg shadow-indigo-500/30 dark:border-indigo-500 dark:bg-indigo-500"
                  : "border-indigo-100/60 bg-white/80 text-gray-700 hover:bg-white hover:text-indigo-700 dark:border-indigo-500/40 dark:bg-indigo-900/30 dark:text-indigo-200 dark:hover:bg-indigo-900/40"
              }`}
            >
              {t("admin.email_validation.tabs.rules", { defaultValue: "Domeinregels" })}
            </button>
          </div>
        </div>

        {activeTab === "checks" ? (
          <EmailValidationChecksPanel refreshToken={checksRefreshToken} />
        ) : (
          <>
        {runFeedback && (
          <div className="rounded-2xl border border-emerald-200/70 bg-emerald-50/70 p-3 text-sm font-semibold text-emerald-900 shadow-sm dark:border-emerald-500/30 dark:bg-emerald-900/20 dark:text-emerald-100">
            {runFeedback}
          </div>
        )}

        {err && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-900/30 dark:text-red-200">
            {typeof err === "string" ? err : err.message || err.title}
          </div>
        )}

        <LdsTableCard>
          <LdsTableScroll>
            <LdsTable>
              <LdsTableHead>
                <LdsTableRow>
                    {columns.length ? (
                      columns.map((c) => (
                        <LdsTableHeadCell key={c}>
                          {headerLabelForColumn(c)}
                        </LdsTableHeadCell>
                      ))
                    ) : (
                      <LdsTableHeadCell>
                        {headerLabelForColumn("domain")}
                      </LdsTableHeadCell>
                    )}
                    <LdsTableHeadCell className="text-right">{t("common.actions.name", { defaultValue: "Actions" })}</LdsTableHeadCell>
                </LdsTableRow>
              </LdsTableHead>
              <LdsTableBody>
                  {rules.length === 0 ? (
                    <LdsTableRow>
                      <LdsTableCell className="text-slate-600 dark:text-slate-300" colSpan={(columns.length || 1) + 1}>
                        {t("common.empty", { defaultValue: "No items." })}
                      </LdsTableCell>
                    </LdsTableRow>
                  ) : (
                    rules.map((r, idx) => {
                      const key = String(pickIdentity(r) ?? idx);
                      return (
                        <LdsTableRow
                          key={key}
                        >
                          {(columns.length ? columns : ["domain"]).map((c) => (
                            <LdsTableCell key={c}>
                              <span className="block max-w-[46ch] truncate" title={typeof r?.[c] === "string" ? r[c] : undefined}>
                                {renderValue(r?.[c])}
                              </span>
                            </LdsTableCell>
                          ))}
                          <LdsTableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              <button
                                type="button"
                                onClick={() => openEdit(r)}
                                className="rounded-full border border-indigo-200/60 bg-indigo-50/70 px-3 py-1 text-sm font-semibold text-indigo-700 transition hover:text-indigo-900 dark:border-indigo-500/40 dark:bg-indigo-900/30 dark:text-indigo-200"
                              >
                                {t("common.actions.edit", { defaultValue: "Edit" })}
                              </button>
                              <button
                                type="button"
                                onClick={() => requestDelete(r)}
                                className="rounded-full border border-red-200/60 bg-red-50/70 px-3 py-1 text-sm font-semibold text-red-700 transition hover:text-red-900 dark:border-red-500/40 dark:bg-red-900/30 dark:text-red-200"
                              >
                                {t("common.actions.delete", { defaultValue: "Delete" })}
                              </button>
                            </div>
                          </LdsTableCell>
                        </LdsTableRow>
                      );
                    })
                  )}
              </LdsTableBody>
            </LdsTable>
          </LdsTableScroll>
        </LdsTableCard>
          </>
        )}
      </div>

      {editorOpen && (
        <div className="fixed inset-0 z-[9999]">
          <button
            type="button"
            className="absolute inset-0 bg-slate-950/40 backdrop-blur-[1px]"
            onClick={() => setEditorOpen(false)}
            aria-label={t("common.close", { defaultValue: "Close" })}
          />
          <aside className="absolute right-0 top-0 h-full w-full max-w-2xl overflow-y-auto border-l border-indigo-100/60 bg-white/95 p-6 shadow-2xl dark:border-indigo-500/30 dark:bg-slate-950/95">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Email validation
                </div>
                <h3 className="mt-1 truncate text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {editorMode === "edit"
                    ? t("common.actions.edit", { defaultValue: "Bewerken" })
                    : t("admin.email_validation.new_rule", { defaultValue: "Nieuwe regel" })}
                </h3>
              </div>
              <button
                type="button"
                className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 dark:hover:bg-gray-800"
                onClick={() => setEditorOpen(false)}
              >
                {t("common.close", { defaultValue: "Close" })}
              </button>
            </div>

            <div className="mt-6 space-y-4 text-left">
              {editorError && (
                <div className="rounded-2xl border border-red-200/80 bg-red-50/90 px-4 py-3 text-sm text-red-800 shadow-sm dark:border-red-500/40 dark:bg-red-900/30 dark:text-red-100">
                  {editorError}
                </div>
              )}

              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <label className="block">
                  <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    {t("admin.email_validation.domain", { defaultValue: "Domein" })}
                  </span>
                  <input
                    value={form.domain}
                    onChange={(e) => setForm((p) => ({ ...p, domain: e.target.value }))}
                    placeholder="hotmail.com, .test of *.test"
                    className="mt-1 w-full rounded-xl border border-indigo-100/70 bg-white/85 px-3 py-2 text-sm text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-400/60 dark:border-indigo-500/30 dark:bg-gray-900/60 dark:text-gray-100"
                  />
                  <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
                    {t("admin.email_validation.domain_hint", {
                      defaultValue: "Tip: gebruik .test of *.test voor een suffix/wildcard regel (matcht alles wat eindigt op .test).",
                    })}
                  </p>
                </label>

                <label className="block">
                  <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    {t("admin.email_validation.suggested_domain", { defaultValue: "Suggestie (domein)" })}
                  </span>
                  <input
                    value={form.suggestedDomain}
                    onChange={(e) => setForm((p) => ({ ...p, suggestedDomain: e.target.value }))}
                    placeholder="hotmail.com"
                    className="mt-1 w-full rounded-xl border border-indigo-100/70 bg-white/85 px-3 py-2 text-sm text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-400/60 dark:border-indigo-500/30 dark:bg-gray-900/60 dark:text-gray-100"
                  />
                  <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
                    {t("admin.email_validation.suggested_hint", {
                      defaultValue: "Laat leeg als er geen suggestie nodig is.",
                    })}
                  </p>
                </label>

                <label className="block">
                  <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    {t("admin.email_validation.status", { defaultValue: "Status" })}
                  </span>
                  <select
                    value={form.status}
                    onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))}
                    className="mt-1 w-full rounded-xl border border-indigo-100/70 bg-white/85 px-3 py-2 text-sm text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-400/60 dark:border-indigo-500/30 dark:bg-gray-900/60 dark:text-gray-100"
                  >
                    <option value="suspicious">{t("admin.email_validation.status.suspicious", { defaultValue: "suspicious (twijfelachtig)" })}</option>
                    <option value="invalid">{t("admin.email_validation.status.invalid", { defaultValue: "invalid (afkeuren)" })}</option>
                    <option value="valid">{t("admin.email_validation.status.valid", { defaultValue: "valid (toestaan)" })}</option>
                  </select>
                </label>

                <label className="block">
                  <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    {t("admin.email_validation.match_type", { defaultValue: "Match type" })}
                  </span>
                  <select
                    value={form.matchType}
                    onChange={(e) => setForm((p) => ({ ...p, matchType: e.target.value }))}
                    className="mt-1 w-full rounded-xl border border-indigo-100/70 bg-white/85 px-3 py-2 text-sm text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-400/60 dark:border-indigo-500/30 dark:bg-gray-900/60 dark:text-gray-100"
                    disabled={deriveMatchTypeFromDomainInput(form.domain) === "SUFFIX"}
                    title={
                      deriveMatchTypeFromDomainInput(form.domain) === "SUFFIX"
                        ? t("admin.email_validation.match_type_locked", { defaultValue: "Wildcard domein gedetecteerd; match type is SUFFIX." })
                        : undefined
                    }
                  >
                    <option value="">{t("common.auto", { defaultValue: "Auto" })}</option>
                    <option value="EXACT">{t("admin.email_validation.match_type.exact", { defaultValue: "EXACT (alleen dit domein)" })}</option>
                    <option value="SUFFIX">{t("admin.email_validation.match_type.suffix", { defaultValue: "SUFFIX (alles wat eindigt op dit domein)" })}</option>
                  </select>
                  <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
                    {deriveMatchTypeFromDomainInput(form.domain) === "SUFFIX"
                      ? t("admin.email_validation.match_type_auto_suffix", { defaultValue: "Bij .xxx of *.xxx wordt SUFFIX automatisch gebruikt." })
                      : t("admin.email_validation.match_type_hint", { defaultValue: "Laat op Auto staan tenzij je wilt forceren." })}
                  </p>
                </label>

                <label className="block md:col-span-2">
                  <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    {t("admin.email_validation.reason", { defaultValue: "Reden" })}
                  </span>
                  <input
                    value={form.reason}
                    onChange={(e) => setForm((p) => ({ ...p, reason: e.target.value }))}
                    placeholder="typo_domain"
                    className="mt-1 w-full rounded-xl border border-indigo-100/70 bg-white/85 px-3 py-2 text-sm text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-400/60 dark:border-indigo-500/30 dark:bg-gray-900/60 dark:text-gray-100"
                  />
                  <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
                    {t("admin.email_validation.reason_hint", { defaultValue: "Vrij veld (bijv. typo_domain, reserved_domain)." })}
                  </p>
                </label>
              </div>

              <label className="flex items-center gap-3 rounded-2xl border border-indigo-100/70 bg-white/70 px-4 py-3 text-sm text-gray-800 shadow-sm dark:border-indigo-500/30 dark:bg-gray-900/40 dark:text-gray-100">
                <input
                  type="checkbox"
                  checked={Boolean(form.enabled)}
                  onChange={(e) => setForm((p) => ({ ...p, enabled: e.target.checked }))}
                  className="h-4 w-4"
                />
                <span className="font-semibold">{t("admin.email_validation.enabled", { defaultValue: "Ingeschakeld" })}</span>
              </label>

              <details
                className="rounded-2xl border border-indigo-100/70 bg-white/70 px-4 py-3 text-sm shadow-sm dark:border-indigo-500/30 dark:bg-gray-900/40"
                open={advancedOpen}
                onToggle={(e) => setAdvancedOpen(e.currentTarget.open)}
              >
                <summary className="cursor-pointer font-semibold text-indigo-700 dark:text-indigo-200">
                  {t("common.advanced", { defaultValue: "Geavanceerd (JSON preview)" })}
                </summary>
                <pre className="mt-3 overflow-auto rounded-xl bg-gray-900 p-3 text-xs text-gray-100">
                  {JSON.stringify(
                    {
                      ...(editorMode !== "edit" && form.id != null ? { id: form.id } : null),
                      domain: normalizeDomainInputForDisplay(form.domain),
                      ...(apiPrefersSnakeCase
                        ? { suggested_domain: String(form.suggestedDomain || "").trim() || null }
                        : { suggestedDomain: String(form.suggestedDomain || "").trim() || null }),
                      enabled: Boolean(form.enabled),
                      reason: String(form.reason || "").trim() || null,
                      status: String(form.status || "").trim(),
                      ...(apiPrefersSnakeCase
                        ? {
                            match_type:
                              deriveMatchTypeFromDomainInput(form.domain) ||
                              (String(form.matchType || "").trim().toUpperCase() || null),
                          }
                        : {
                            matchType:
                              deriveMatchTypeFromDomainInput(form.domain) ||
                              (String(form.matchType || "").trim().toUpperCase() || null),
                          }),
                    },
                    null,
                    2
                  )}
                </pre>
              </details>

              <div className="flex flex-wrap justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setEditorOpen(false)}
                  className="rounded-full border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 transition hover:text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
                >
                  {t("common.cancel", { defaultValue: "Annuleren" })}
                </button>
                <button
                  type="button"
                  onClick={saveRule}
                  disabled={saving}
                  className="lumen-glow-button text-sm disabled:opacity-60"
                >
                  {saving
                    ? t("common.saving", { defaultValue: "Opslaan…" })
                    : t("admin.email_validation.save", { defaultValue: "Wijzigingen opslaan" })}
                </button>
              </div>
            </div>
          </aside>
        </div>
      )}

      <Modal
        show={deleteOpen}
        title={t("common.modal.are_you_sure", { defaultValue: "Are you sure?" })}
        message={
          t("admin.email_validation.delete_confirm", {
            defaultValue: "Delete this domain rule?",
          }) + ` ${pickIdentity(activeRule) ?? ""}`
        }
        type="confirm"
        onConfirm={deleteRule}
        onClose={() => setDeleteOpen(false)}
      />
    </LdsOverviewPage>
  );
};

export default AdminEmailValidationDomains;
